// Copyright (c) 2026, Dokos SAS and Contributors
// License: GNU General Public License v3. See license.txt
// @dokos: "New Project" quick entry of the Project Management view: the project, its
// team (one Task Assignment per member over the project dates) and its first tasks,
// created in one request by `create_project_with_plan`.

import { h, safeBoldHtml } from "./PMRender";

const CREATE_METHOD = "erpnext.projects.project_management.create_project_with_plan";

/**
 * @dokos: open the "New Project" dialog.
 * @param {Object} [opts]
 * @param {Object} [opts.values] prefill (project_name, customer, expected_start_date…)
 * @param {function} [opts.on_saved] called with the `create_project_with_plan` result
 * @returns {PMProjectQuickEntry}
 */
export function openProjectQuickEntry(opts = {}) {
	const entry = new PMProjectQuickEntry(opts);
	entry.show();
	return entry;
}

/** @dokos */
export class PMProjectQuickEntry {
	constructor({ values = {}, on_saved = null } = {}) {
		this.values = values || {};
		this.on_saved = on_saved;
	}

	show() {
		const update_summary = frappe.utils.debounce(() => this.render_summary(), 150);
		this.dialog = new frappe.ui.Dialog({
			title: __("New {0}", [__("Project")], "Project"),
			size: "large",
			fields: [
				{
					fieldtype: "Data",
					fieldname: "project_name",
					label: __("Project Name"),
					reqd: 1,
				},
				{
					fieldtype: "Link",
					fieldname: "customer",
					label: __("Customer"),
					options: "Customer",
				},
				{ fieldtype: "Column Break" },
				{
					fieldtype: "Link",
					fieldname: "project_template",
					label: __("From Template"),
					options: "Project Template",
					onchange: update_summary,
				},
				{
					fieldtype: "Date",
					fieldname: "expected_start_date",
					label: __("Expected Start Date"),
				},
				{
					fieldtype: "Date",
					fieldname: "expected_end_date",
					label: __("Expected End Date"),
				},
				{
					fieldtype: "Section Break",
					label: __("Team", null, "PM View"),
					description: __(
						"One Task Assignment per person, over the project dates",
						null,
						"PM View"
					),
				},
				{
					fieldtype: "Table",
					fieldname: "team",
					// @dokos: no label, the section break above already says "Team"
					in_place_edit: true,
					data: [],
					fields: [
						{
							fieldtype: "Link",
							fieldname: "employee",
							label: __("Employee"),
							options: "Employee",
							reqd: 1,
							in_list_view: 1,
							columns: 4,
						},
						{
							fieldtype: "Link",
							fieldname: "activity_type",
							label: __("Activity Type"),
							options: "Activity Type",
							in_list_view: 1,
							columns: 3,
						},
						{
							fieldtype: "Float",
							fieldname: "hours",
							label: __("Hours per day", null, "PM View"),
							in_list_view: 1,
							columns: 2,
						},
					],
				},
				{ fieldtype: "Section Break", label: __("Tasks") },
				{
					fieldtype: "Table",
					fieldname: "tasks",
					// @dokos: no label, the section break above already says "Tasks"
					in_place_edit: true,
					data: [],
					fields: [
						{
							fieldtype: "Data",
							fieldname: "subject",
							label: __("Subject"),
							reqd: 1,
							in_list_view: 1,
							columns: 4,
						},
						{
							fieldtype: "Link",
							fieldname: "assigned_to",
							label: __("Assigned To"),
							options: "Employee",
							in_list_view: 1,
							columns: 2,
						},
						{
							fieldtype: "Link",
							fieldname: "activity_type",
							label: __("Activity Type"),
							options: "Activity Type",
							in_list_view: 1,
							columns: 2,
						},
						{
							fieldtype: "Float",
							fieldname: "expected_time",
							label: __("Expected (h)", null, "PM View"),
							in_list_view: 1,
							columns: 1,
						},
					],
				},
				{ fieldtype: "Section Break" },
				{ fieldtype: "HTML", fieldname: "summary_html" },
			],
			primary_action_label: __("Save"),
			primary_action: () => this.save(),
		});
		this.dialog.$wrapper.addClass("pm-project-dialog");
		this.dialog.add_custom_action(__("Edit Full Form"), () => this.open_full_form());

		// The grids have no change hook of their own: follow edits and row add/remove.
		this.dialog.$wrapper.on("change input", update_summary);
		this.dialog.$wrapper.on("click", ".grid-add-row, .grid-remove-rows, .grid-delete-row", () =>
			setTimeout(update_summary, 0)
		);
		this.dialog.$wrapper.on("keydown", (e) => {
			if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && !frappe.request.ajax_count) {
				e.preventDefault();
				this.dialog.get_primary_btn().trigger("click");
			}
		});

		this.dialog.show();
		this.dialog.set_values(this.values);
		this.render_summary();
	}

	get_rows(fieldname, required) {
		return (this.dialog.get_value(fieldname) || []).filter((row) => row[required]);
	}

	render_summary() {
		const wrapper = this.dialog.fields_dict.summary_html.$wrapper[0];
		const tasks = this.get_rows("tasks", "subject").length;
		const assignments = this.get_rows("team", "employee").length;
		let text = __("Creates 1 Project, {0} Tasks, {1} Task Assignments", [tasks, assignments], "PM View");
		if (this.dialog.get_value("project_template")) {
			text += " " + __("(plus the tasks of the template)", null, "PM View");
		}
		wrapper.replaceChildren(
			h("div", { class: "pm-dialog-summary dk-muted", attrs: { "aria-live": "polite" }, text })
		);
	}

	validate(values, team) {
		if (team.length && !(values.expected_start_date && values.expected_end_date)) {
			frappe.msgprint(
				__(
					"Please set the expected start and end dates of the project to plan its team",
					null,
					"PM View"
				)
			);
			return false;
		}
		if (
			values.expected_start_date &&
			values.expected_end_date &&
			values.expected_end_date < values.expected_start_date
		) {
			frappe.msgprint(__("The end date must be after the start date"));
			return false;
		}
		const missing = team.find((row) => !(Number(row.hours) > 0));
		if (missing) {
			frappe.msgprint(__("Please set the hours per day of {0}", [missing.employee], "PM View"));
			return false;
		}
		return true;
	}

	async save() {
		const values = this.dialog.get_values();
		if (!values) return;
		const team = this.get_rows("team", "employee").map((row) => ({
			employee: row.employee,
			activity_type: row.activity_type || null,
			hours: Number(row.hours) || 0,
		}));
		const tasks = this.get_rows("tasks", "subject").map((row) => ({
			subject: row.subject,
			assigned_to: row.assigned_to || null,
			activity_type: row.activity_type || null,
			expected_time: Number(row.expected_time) || 0,
		}));
		if (!this.validate(values, team)) return;

		this.dialog.disable_primary_action();
		try {
			const result = await frappe.xcall(CREATE_METHOD, {
				project: {
					project_name: values.project_name,
					customer: values.customer || null,
					project_template: values.project_template || null,
					expected_start_date: values.expected_start_date || null,
					expected_end_date: values.expected_end_date || null,
				},
				team,
				tasks,
			});
			this.dialog.hide();
			const link = h("a", {
				href: frappe.utils.get_form_link("Project", result.project),
				text: result.project,
			});
			frappe.show_alert({
				message: __("Project {0} created", [link.outerHTML], "PM View"),
				indicator: "green",
			});
			for (const warning of result.warnings || []) {
				frappe.show_alert({ message: safeBoldHtml(warning), indicator: "orange" }, 8);
			}
			this.on_saved?.(result);
		} finally {
			this.dialog.enable_primary_action();
		}
	}

	open_full_form() {
		const values = this.dialog.get_values(true) || {};
		this.dialog.hide();
		const fields = [
			"project_name",
			"customer",
			"project_template",
			"expected_start_date",
			"expected_end_date",
		];
		frappe.new_doc(
			"Project",
			Object.fromEntries(fields.filter((f) => values[f]).map((f) => [f, values[f]]))
		);
	}
}
